import { useApiBase } from './useApiBase'
import { CoverTooLargeError, CoverUploadFailedError } from '~/types/errors'

export type WorkVisibility = 'public' | 'unlisted' | 'private'

export interface Work {
  workId: string
  title: string
  author: string
  /**
   * Creator-page slug (#417). null/undefined = the author hasn't set up a
   * public `/c/<slug>` page yet; render the author name as plain text.
   */
  authorSlug?: string | null
  description: string
  description2?: string
  publishedAt: string
  plays: number
  likes: number
  liked: boolean
  /**
   * #724: viewer がこの作品をお気に入り登録しているか。お気に入りはサインイン必須
   * （いいねと違い匿名不可）で、数は公開しないため count は持たない。未ログイン
   * 視聴者には常に false。
   */
  favorited?: boolean
  publicUrl: string
  coverImageUrl?: string
  /**
   * #763: ギャラリー表示用の animated WebP。**正本は `coverImageUrl` の GIF**で、
   * 作者のダウンロード・X 共有・Pyxel 公式 User Examples 投稿・OGP はそちらを使う。
   * undefined = 未生成（旧 Work / backfill 前 / 変換失敗）→ GIF にフォールバック。
   */
  coverWebpUrl?: string
  /**
   * #707: X(Twitter) OGP / twitter:image 用の静止画 PNG。カバー GIF とは別枠で、
   * クリスタル化時に native screenshot(scale=2) から作る。未生成 (旧 Work /
   * backfill 前) は undefined → カバー GIF にフォールバック。
   */
  ogpImageUrl?: string
  keywords?: string[]
  remixable: boolean
  gamepadCompatible: boolean
  visibility?: WorkVisibility
  slug?: string
  alreadyDecoded?: boolean
  userId?: string
}

export interface LikeWorkResponse {
  likes: number
  liked: boolean
}

export interface FavoriteWorkResponse {
  favorited: boolean
}

export interface DecodeWorkResponse {
  workId: string
  slug: string
  groupId: string
  mpCharged: number
  alreadyDecoded: boolean
}

// #724: 'favorites' は viewer のお気に入りだけを返すサインイン必須スコープ。
export type WorksSortKey = 'featured' | 'popular' | 'new' | 'favorites'

export interface ListWorksResponse {
  works: Work[]
  total: number
  page: number
  perPage: number
}

export interface CreateSessionResponse {
  sessionId: string
  sessionUrl: string
  /**
   * #663: Pyodide built-in packages the entrypoint imports (e.g. ["numpy"]).
   * Forwarded to `<pyxel-play packages="...">` so Pyxel/Pyodide preloads them
   * via `pyodide.loadPackage()`. Empty / omitted means no extra packages.
   */
  pyodidePackages?: string[]
  /**
   * #450: cloud-save scope (app identity), resolved server-side from the session.
   * Used to namespace localStorage for unauthenticated players; null when the
   * route has no cloud save. The authenticated path only needs the session id.
   */
  saveScope?: string | null
  /**
   * #764: ビルドされた pyxapp のサイズ。プレビューのために既にビルドしているので
   * 追加コストはゼロ。クリスタル化を押す前に「この作品の大きさ」を見せるための値。
   */
  sizeBytes?: number | null
}

export interface ScreeningResult {
  approved: boolean
  reason?: string | null
}

export interface GenerateSlugResponse {
  slug: string
  screening?: ScreeningResult | null
  codeScreening?: ScreeningResult | null
}

// #750: 公開作品の「つくった人たち」表示用。null フィールドは backend が
// exclude_none で省くため、slug / sourceUrl / role は optional。
export interface CreditPerson {
  name: string
  /**
   * #417 / #832: creator-page slug。**サーバが実在を検証したものだけ**が入るので、
   * あればそのまま `/c/<slug>` にリンクしてよい（未設定なら plain text）。
   * userId は公開エンドポイントに出さない (#832)。
   */
  slug?: string
}

export interface CreditLine {
  /** リソース区分（code / image / map / sound / music 等）。 */
  category: string
  /** 表示ラベル（例:「プログラム」「ドット絵」）。 */
  label: string
  creator: CreditPerson
  license: string
  licenseLabel: string
  /** #836: バッジの短い名前（CC0 / 要クレジット / C&M内のみ 等）。 */
  licenseBadge?: string
  /** #836: 義務の重さ。free=緑 / credit=琥珀 / limited=灰。 */
  licenseTone?: 'free' | 'credit' | 'limited'
  /** #836: 条文へのリンク。標準ライセンスは公式、C&M 独自は /licenses/<slug>。 */
  licenseUrl?: string
  /** #835: ライセンスが著作表記を義務づけるか（魔王魂等）。表示の出し分けに使う。 */
  attributionRequired?: boolean
  sourceUrl?: string
  role?: string
  editors: CreditPerson[]
}

export interface WorkCredits {
  projectId: string
  lines: CreditLine[]
  /** #835: 1 行でも著作表記が必須なら true。true のときクレジットは隠せない。 */
  attributionRequired?: boolean
}

export interface CoverUploadTarget {
  url: string
  fields: Record<string, string>
  key: string
  maxBytes: number
}

export interface CoverUploadsResponse {
  cover: CoverUploadTarget
  ogp: CoverUploadTarget
}

/** クリスタル化リクエストに載せる表紙画像の「キー」(#762)。 */
export interface CoverImageKeys {
  thumbnailKey?: string
  ogpImageKey?: string
}

export function useWorkApi() {
  const { apiBase, authenticatedFetch, publicFetch } = useApiBase()

  /**
   * #802: `seed` は「注目」タブのシャッフルを再現するための種。**ページを跨いで
   * 同じ並びを保つために要る**（無いとページごとに違うシャッフルになり、追記
   * スクロールで同じ作品が 2 枚並ぶ）。1 ページしか取らない面では省略してよい。
   */
  const listWorks = async (
    page?: number,
    perPage?: number,
    sort?: WorksSortKey,
    seed?: number,
  ) => {
    return authenticatedFetch<ListWorksResponse>(
      `${apiBase}/api/v1/works`,
      {
        method: 'GET',
        query: { page, perPage, sort, seed },
      },
    )
  }

  const getPublicWork = async (slug: string) => {
    return publicFetch<Work>(
      `${apiBase}/api/v1/public/works/${slug}`,
      { method: 'GET' },
    )
  }

  // #750: 公開作品の per-resource attribution。public（未ログインでも見える）。
  const getWorkCredits = async (slug: string) => {
    return publicFetch<WorkCredits>(
      `${apiBase}/api/v1/public/works/${slug}/credits`,
      { method: 'GET' },
    )
  }

  // #843: 未認証の公開ギャラリー（`/works`）用。`seed` の意味は `listWorks` と同じで、
  // 「注目」タブのシャッフルをページ跨ぎで再現するために要る（渡さないとページごとに
  // 違う並びになり、追記スクロールで同じ作品が 2 枚出る）。
  const listPublicWorks = async (
    page?: number,
    perPage?: number,
    sort?: WorksSortKey,
    seed?: number,
  ) => {
    return publicFetch<ListWorksResponse>(
      `${apiBase}/api/v1/public/works`,
      {
        method: 'GET',
        query: { page, perPage, sort, seed },
      },
    )
  }

  const createProjectSession = async (projectId: string, entrypoint?: string, screening?: boolean) => {
    return authenticatedFetch<CreateSessionResponse>(
      `${apiBase}/api/v1/sessions`,
      {
        method: 'POST',
        body: JSON.stringify({
          projectId,
          anonymousId: useAnonymousInfo().anonymousId,
          ...(entrypoint != null ? { entrypoint } : {}),
          ...(screening ? { screening: true } : {}),
        }),
      },
    )
  }

  const createWorkSession = async (workId: string) => {
    return authenticatedFetch<CreateSessionResponse>(
      `${apiBase}/api/v1/sessions`,
      {
        method: 'POST',
        body: JSON.stringify({
          workId,
          anonymousId: useAnonymousInfo().anonymousId,
        }),
      },
    )
  }

  const createPublicSession = async (slug: string) => {
    return publicFetch<CreateSessionResponse>(
      `${apiBase}/api/v1/public/sessions`,
      {
        method: 'POST',
        body: JSON.stringify({
          slug,
          anonymousId: useAnonymousInfo().anonymousId,
        }),
      },
    )
  }

  const createSessionClose = async (sessionId: string) => {
    await publicFetch(
      `${apiBase}/api/v1/sessions/${sessionId}/close`,
      {
        method: 'POST',
        body: JSON.stringify({
          anonymousId: useAnonymousInfo().anonymousId,
        }),
      },
    )
  }

  const generateSlug = async (
    title: string,
    description?: string,
    projectId?: string,
    entrypoint?: string,
  ) => {
    return authenticatedFetch<GenerateSlugResponse>(
      `${apiBase}/api/v1/works/generate-slug`,
      {
        method: 'POST',
        body: JSON.stringify({
          title,
          description,
          ...(projectId != null ? { projectId } : {}),
          ...(entrypoint != null ? { entrypoint } : {}),
        }),
      },
    )
  }

  /**
   * 表紙画像 (カバー GIF / OGP 静止画) を S3 へ直接アップロードし、
   * クリスタル化リクエストに載せる「キー」を返す (#762)。
   *
   * これらを base64 data URL のまま `POST /api/v1/events/trigger` の body に
   * 載せると、Lambda 同期呼び出しの payload 上限 (実効 6MB) を超えて **413** に
   * なる。カバー GIF の大きさは Pyxel の画面解像度 × キャプチャ秒数で決まるので、
   * 作品データを削っても直らない。body を通らない経路が唯一の恒久策。
   *
   * アップロード先のキーは**サーバが決める**。ここでは受け取った署名情報を
   * そのまま multipart に載せるだけで、任意のパスは指定できない。PUT ではなく
   * POST なのは、`content-length-range` 条件でサイズ上限を S3 側に強制させる
   * ため (presigned PUT にはその手段が無い)。
   *
   * `fields` は署名の一部なので**改変せずそのまま**、かつ `file` より**前に**
   * 詰める (S3 は file 以降のフィールドを無視する)。`Authorization` ヘッダも
   * `Content-Type` も付けない — 権限の実体は署名。
   */
  const uploadCoverImages = async (
    thumbnailData?: string,
    ogpImageData?: string,
  ): Promise<CoverImageKeys> => {
    if (!thumbnailData && !ogpImageData) return {}

    const targets = await authenticatedFetch<CoverUploadsResponse>(
      `${apiBase}/api/v1/works/cover-uploads`,
      { method: 'POST', body: JSON.stringify({}) },
    )

    const post = async (target: CoverUploadTarget, dataUrl: string): Promise<string> => {
      const blob = await (await fetch(dataUrl)).blob()
      if (blob.size > target.maxBytes) {
        const mb = (n: number) => Math.round(n / 1024 / 1024)
        throw new CoverTooLargeError(
          `表紙の画像が大きすぎます（${mb(blob.size)}MB / 上限 ${mb(target.maxBytes)}MB）。`
          + 'キャプチャを短くするか、画面サイズを小さくしてお試しください。',
        )
      }
      const form = new FormData()
      for (const [k, v] of Object.entries(target.fields)) form.append(k, v)
      form.append('file', blob)
      let res: Response
      try {
        res = await fetch(target.url, { method: 'POST', body: form })
      }
      catch (e) {
        console.warn('[cover-upload] network error', e)
        throw new CoverUploadFailedError()
      }
      if (!res.ok) {
        console.warn('[cover-upload] S3 rejected the upload', res.status, await res.text().catch(() => ''))
        throw new CoverUploadFailedError()
      }
      return target.key
    }

    const [thumbnailKey, ogpImageKey] = await Promise.all([
      thumbnailData ? post(targets.cover, thumbnailData) : Promise.resolve(undefined),
      ogpImageData ? post(targets.ogp, ogpImageData) : Promise.resolve(undefined),
    ])

    return {
      ...(thumbnailKey ? { thumbnailKey } : {}),
      ...(ogpImageKey ? { ogpImageKey } : {}),
    }
  }

  const listMyWorks = async (page?: number, perPage?: number) => {
    return authenticatedFetch<ListWorksResponse>(
      `${apiBase}/api/v1/my/works`,
      {
        method: 'GET',
        query: { page, perPage },
      },
    )
  }

  const updateWorkVisibility = async (workId: string, visibility: WorkVisibility) => {
    return authenticatedFetch<undefined>(
      `${apiBase}/api/v1/my/works/${workId}/visibility`,
      {
        method: 'PUT',
        body: JSON.stringify({ visibility }),
      },
    )
  }

  const getWork = async (slug: string) => {
    return authenticatedFetch<Work>(
      `${apiBase}/api/v1/works/${slug}`,
      { method: 'GET' },
    )
  }

  // #751: いいねはログイン必須（#697 の匿名いいねを撤回）。常に authenticatedFetch。
  // 未ログインでの♥は呼び出し側がサインイン CTA に誘導する（この関数は呼ばれない）。
  const likeWork = async (slug: string) => {
    return authenticatedFetch<LikeWorkResponse>(
      `${apiBase}/api/v1/works/${slug}/like`,
      { method: 'POST' },
    )
  }

  /**
   * 公開作品への通報（#940）。サインインしていなくても送れる（そのときは email 必須）。
   * サインインしていればアカウントのメールアドレスに控えが届くので、email は送らない。
   */
  const reportWork = async (
    slug: string,
    request: { reason: 'rights' | 'inappropriate' | 'other', message: string, email?: string },
    signedIn: boolean,
  ) => {
    const url = `${apiBase}/api/v1/public/works/${encodeURIComponent(slug)}/reports`
    const options = { method: 'POST' as const, body: JSON.stringify(request) }
    const fetcher = signedIn ? authenticatedFetch : publicFetch
    return fetcher<{ reportId: string, reporterEmail: string }>(url, options)
  }

  const unlikeWork = async (slug: string) => {
    return authenticatedFetch<LikeWorkResponse>(
      `${apiBase}/api/v1/works/${slug}/like`,
      { method: 'DELETE' },
    )
  }

  const decodeWork = async (slug: string) => {
    return authenticatedFetch<DecodeWorkResponse>(
      `${apiBase}/api/v1/works/${slug}/decode`,
      { method: 'POST' },
    )
  }

  // #724: お気に入りはサインイン必須（いいねと違い匿名経路は無い）。常に
  // authenticatedFetch を使う。未ログインで呼ぶと 401 になるため、呼び出し側で
  // 認証済みを保証すること（WorkFavoriteButton が isAuthenticated でガードする）。
  const favoriteWork = async (slug: string) => {
    return authenticatedFetch<FavoriteWorkResponse>(
      `${apiBase}/api/v1/works/${slug}/favorite`,
      { method: 'POST' },
    )
  }

  const unfavoriteWork = async (slug: string) => {
    return authenticatedFetch<FavoriteWorkResponse>(
      `${apiBase}/api/v1/works/${slug}/favorite`,
      { method: 'DELETE' },
    )
  }

  return {
    listWorks,
    getPublicWork,
    getWorkCredits,
    listPublicWorks,
    listMyWorks,
    updateWorkVisibility,
    getWork,
    likeWork,
    unlikeWork,
    reportWork,
    favoriteWork,
    unfavoriteWork,
    decodeWork,
    createProjectSession,
    createWorkSession,
    createPublicSession,
    createSessionClose,
    generateSlug,
    uploadCoverImages,
  }
}
